import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from '@proptr/api-client/react';
import { LoginPage } from './pages/LoginPage.js';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage.js';
import { OnboardingPage } from './pages/OnboardingPage.js';
import { ResetPasswordPage } from './pages/ResetPasswordPage.js';
import { AffiliatePage } from './pages/AffiliatePage.js';
import { AffiliateSettingsPage } from './pages/AffiliateSettingsPage.js';
import { AgencyProptreeSettingsPage } from './pages/AgencyProptreeSettingsPage.js';
import { BillingPage } from './pages/BillingPage.js';
import { AgentsProptreeSettingsPage } from './pages/AgentsProptreeSettingsPage.js';
import { AgentsPage } from './pages/AgentsPage.js';
import { ContactsPage } from './pages/ContactsPage.js';
import { FeedEditPage } from './pages/FeedEditPage.js';
import { CampaignEditPage } from './pages/CampaignEditPage.js';
import { PostEditPage } from './pages/PostEditPage.js';
import { MarketingPage } from './pages/MarketingPage.js';
import { RenderPage } from './pages/RenderPage.js';
import { SystemLogsPage } from './pages/SystemLogsPage.js';
import { CrmSettingsPage } from './pages/CrmSettingsPage.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { FormEditPage } from './pages/FormEditPage.js';
import { FormsPage } from './pages/FormsPage.js';
import { FaqPage } from './pages/FaqPage.js';
import { ProfilePage } from './pages/ProfilePage.js';
import { AgencySettingsPage } from './pages/AgencySettingsPage.js';
import { InquiriesPage } from './pages/InquiriesPage.js';
import { ListingsPage } from './pages/ListingsPage.js';
import { ProptreePage } from './pages/ProptreePage.js';

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
      <Route
        path="/forgot-password"
        element={<PublicOnly><ForgotPasswordPage /></PublicOnly>}
      />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* Public — landed here from the signup verification email's magic
          link. The page itself consumes the token and signs the user in. */}
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route
        path="/"
        element={<RequireManager><DashboardPage /></RequireManager>}
      />
      <Route
        path="/faq"
        element={<RequireManager><FaqPage /></RequireManager>}
      />
      <Route
        path="/settings/profile"
        element={<RequireManager><ProfilePage /></RequireManager>}
      />
      <Route
        path="/settings/agency"
        element={<RequireManager><AgencySettingsPage /></RequireManager>}
      />
      <Route
        path="/listings"
        element={<RequireManager><ListingsPage /></RequireManager>}
      />
      <Route
        path="/proptree"
        element={<RequireManager><ProptreePage /></RequireManager>}
      />
      <Route
        path="/proptree/agency"
        element={
          <RequireManager>
            <AgencyProptreeSettingsPage />
          </RequireManager>
        }
      />
      <Route
        path="/proptree/agents"
        element={
          <RequireManager>
            <AgentsProptreeSettingsPage />
          </RequireManager>
        }
      />
      <Route
        path="/affiliate"
        element={<RequireManager><AffiliatePage /></RequireManager>}
      />
      <Route
        path="/affiliate/:id/settings"
        element={
          <RequireManager>
            <AffiliateSettingsPage />
          </RequireManager>
        }
      />
      <Route
        path="/forms"
        element={<RequireManager><FormsPage /></RequireManager>}
      />
      <Route
        path="/forms/:id"
        element={<RequireManager><FormEditPage /></RequireManager>}
      />
      <Route
        path="/agents"
        element={<RequireManager><AgentsPage /></RequireManager>}
      />
      <Route
        path="/inquiries"
        element={<RequireManager><InquiriesPage /></RequireManager>}
      />
      <Route
        path="/contacts"
        element={<RequireManager><ContactsPage /></RequireManager>}
      />
      <Route
        path="/marketing"
        element={<RequireManager><MarketingPage /></RequireManager>}
      />
      <Route path="/render/:template" element={<RenderPage />} />
      <Route
        path="/marketing/post/new"
        element={<RequireManager><PostEditPage /></RequireManager>}
      />
      <Route
        path="/marketing/campaign/new"
        element={<RequireManager><CampaignEditPage /></RequireManager>}
      />
      <Route
        path="/marketing/feed/new"
        element={<RequireManager><FeedEditPage /></RequireManager>}
      />
      <Route
        path="/marketing/feed/:id"
        element={<RequireManager><FeedEditPage /></RequireManager>}
      />
      <Route
        path="/system-logs"
        element={<RequireManager><SystemLogsPage /></RequireManager>}
      />
      <Route
        path="/settings"
        element={<RequireManager><CrmSettingsPage /></RequireManager>}
      />
      <Route
        path="/billing"
        element={<RequireManager><BillingPage /></RequireManager>}
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function RequireManager({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  if (user.role !== 'manager') {
    return (
      <main className="grid min-h-screen place-items-center px-6 text-center">
        <div className="space-y-3">
          <div className="eyebrow">Access denied</div>
          <h1 className="font-display text-[32px] font-medium leading-none tracking-[-0.02em]">
            Not authorized
          </h1>
          <p className="font-mono text-[11px] text-muted-foreground">
            You are signed in as <code className="text-foreground">{user.role}</code>. This
            portal requires the <code className="text-foreground">manager</code> role.
          </p>
        </div>
      </main>
    );
  }
  return <>{children}</>;
}

function PublicOnly({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user && user.role === 'manager') return <Navigate to="/" replace />;
  return <>{children}</>;
}
